'use client';

import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';
import { ReactNode, isValidElement, useEffect, useMemo, useState } from 'react';
import { IconExpandLess, IconExpandMore } from '../../Icons';

export type SidebarMenuItemProps = {
  path: string;
  title: string;
  icon?: ReactNode;
  endIcon?: ReactNode;
  childRoutes?: any;
  badgeCount?: number;
  handleClose?: () => void;
  /**
   * Current active path (from routing system)
   */
  currentPath?: string;
  /**
   * Function to check if a path is active
   */
  isPathActive?: (path: string) => boolean;
  /**
   * Navigation function to call when item is clicked. `fromKeyboard` is true when the
   * activation was a keyboard press (Enter/Space) rather than a mouse click.
   */
  onNavigate?: (path: string, fromKeyboard?: boolean) => void;
};

const SidebarMenuItem = ({
  path,
  title,
  icon,
  endIcon,
  childRoutes,
  handleClose,
  currentPath,
  isPathActive,
  onNavigate,
}: SidebarMenuItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const hasChildRoutes = useMemo(
    () => childRoutes && childRoutes.length > 0,
    [childRoutes],
  );

  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;

  useEffect(() => {
    const active = isPathActive
      ? isPathActive(path)
      : currentPath === path || currentPath?.startsWith(path + '/');
    setIsActive(!!active);
  }, [currentPath, path, isPathActive]);

  useEffect(() => {
    setIsExpanded(isActive);
  }, [isActive]);

  const itemStyle: SxProps<Theme> | undefined = {
    '& .MuiListItemText-primary': {
      color: isActive ? palette.text.primary : palette.text.secondary,
    },
    '&.Mui-selected .MuiListItemText-primary': {
      color: palette.text.primary,
    },
    '&:hover .MuiListItemButton-root': {
      boxShadow: 'none',
    },
  };

  return (
    <>
      <Box
        className='SidebarMenuItem-root'
        sx={[
          {
            display: 'flex',
            ':hover': {
              boxShadow: `inset 0 0 0 999px ${palette.action.hover}`,
            },
            ':active': {
              boxShadow: `inset 0 0 0 999px ${palette.action.pressed}`,
            },
            'a:hover': {
              boxShadow: 'none',
            },
            borderRadius: 0,
            mb: 1,
            ...itemStyle,
          },
          isActive
            ? {
                borderLeft: `4px solid ${palette.accent.main}`,
                background: `${palette.action.selected}`,
              }
            : {
                borderLeft: '4px solid transparent',
                background: 'transparent',
              },
        ]}
        data-tour={`nav-${title}`}
      >
        <ListItemButton
          className='SidebarMenuItem-button'
          sx={{
            background: 'transparent',
            borderRadius: 0,
            ':hover': { background: 'transparent' },
            pr: 0,
          }}
          href={path}
          onClick={(evt) => {
            evt.preventDefault();
            onNavigate?.(path, evt.detail === 0);
            handleClose?.();
          }}
        >
          {isValidElement(icon) ? (
            <ListItemIcon className='SidebarMenuItem-icon'>{icon}</ListItemIcon>
          ) : null}
          <ListItemText
            className='SidebarMenuItem-text'
            primary={title}
            slotProps={{
              primary: {
                sx: { fontWeight: isActive ? 600 : 400 },
              },
            }}
          />
          {isValidElement(endIcon) ? (
            <ListItemIcon
              className='SidebarMenuItem-endIcon'
              sx={{ minWidth: 'auto', mr: 2 }}
            >
              {endIcon}
            </ListItemIcon>
          ) : null}
        </ListItemButton>
        {hasChildRoutes && (
          <IconButton
            className='SidebarMenuItem-expand'
            onClick={(evt) => {
              evt.preventDefault();
              evt.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            sx={{ m: 1 }}
            aria-label={
              isExpanded
                ? `Stäng undermeny för ${title}`
                : `Öppna undermeny för ${title}`
            }
          >
            {isExpanded ? <IconExpandLess /> : <IconExpandMore />}
          </IconButton>
        )}
      </Box>
      {hasChildRoutes ? (
        <Collapse
          className='SidebarMenuItem-children'
          in={isExpanded}
          timeout='auto'
          unmountOnExit
        >
          <List component='div' disablePadding>
            {childRoutes.map((menuItem: any, index: number) => (
              <Box key={menuItem.title + index} sx={{ px: 1, py: 1 }}>
                <ListItemButton
                  key={menuItem.title + index}
                  sx={{
                    pl: 2,
                    borderRadius: '4px',
                    minHeight: '46px',
                    ...itemStyle,
                    '&.Mui-selected': {
                      background: `rgba(0, 0, 0, ${theme.palette.action.selectedOpacity})`,
                      borderRadius: '4px',
                    },
                  }}
                  href={menuItem.path}
                  onClick={(evt) => {
                    evt.preventDefault();
                    onNavigate?.(menuItem.path, evt.detail === 0);
                    handleClose?.();
                  }}
                  selected={menuItem.path === currentPath}
                >
                  <>
                    <ListItemText
                      primary={menuItem.title}
                      slotProps={{
                        primary: {
                          variant:
                            menuItem.path === currentPath
                              ? 'subtitle2'
                              : 'body2',
                        },
                      }}
                      sx={{ m: 0 }}
                    />
                  </>
                </ListItemButton>
              </Box>
            ))}
          </List>
        </Collapse>
      ) : null}
    </>
  );
};

export { SidebarMenuItem };
